// src/components/CustomerRentalPricingList.js
import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';
import Select from 'react-select';

// ===== Helpers =====
const moneyStr = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  return String(Number(v).toFixed(2)).replace('.', ',');
};

const sortPricings = (rows = []) =>
  rows.slice().sort((a, b) => {
    const ua = (a.unit?.name || '').toLowerCase();
    const ub = (b.unit?.name || '').toLowerCase();
    if (ua < ub) return -1;
    if (ua > ub) return 1;
    return (a.minQuantity ?? 0) - (b.minQuantity ?? 0);
  });

// ===== Component =====
const CustomerRentalPricingList = () => {
  // Kunde (Ziel)
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Typen (immer als Liste)
  const [machineTypes, setMachineTypes] = useState([]);

  // Accordion
  const [expanded, setExpanded] = useState(() => new Set());

  // Preise je Typ (lazy)
  const [pricingsByType, setPricingsByType] = useState({});   // { [typeId]: rows[] }
  const [loadingByType, setLoadingByType] = useState({});     // { [typeId]: boolean }

  // Edit
  const [editingKey, setEditingKey] = useState(null);         // { machineTypeId, typePricingId }
  const [editValue, setEditValue] = useState('');             // "200,00"

  // Copy UI
  const [copyFromCustomer, setCopyFromCustomer] = useState(null);
  const [copyOnlyType, setCopyOnlyType] = useState(null); // {value,label} für machineType oder null = alle
  const [copyOverwrite, setCopyOverwrite] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);

  // ===== Load initial =====
  useEffect(() => {
    const load = async () => {
      try {
        const [custRes, typeRes] = await Promise.all([api.get('/customers'), api.get('/machine-types')]);
        setCustomers(custRes.data || []);
        setMachineTypes((typeRes.data || []).slice().sort((a, b) => String(a.name).localeCompare(String(b.name))));
      } catch (e) {
        console.error(e);
        alert('Fehler beim Laden von Kunden/Typen.');
      }
    };
    load();
  }, []);

  const customerOptions = useMemo(
    () =>
      (customers || []).map((c) => ({
        value: c.id,
        label: c.company || `${c.firstName || ''} ${c.lastName || ''}`.trim(),
      })),
    [customers]
  );

  const machineTypeOptions = useMemo(
    () => (machineTypes || []).map(t => ({ value: t.id, label: t.name })),
    [machineTypes]
  );

  // ===== Data helpers =====
  const isExpanded = (typeId) => expanded.has(typeId);

  const fetchTypeRows = async (customerId, typeId) => {
    const res = await api.get(`/customer-rental-pricing/${customerId}/${typeId}`);
    return sortPricings(res.data || []);
  };

  const reloadExpandedTypes = async () => {
    if (!selectedCustomer?.value) return;
    const ids = Array.from(expanded);
    if (!ids.length) return;
    const loads = await Promise.all(
      ids.map(async (typeId) => {
        try {
          const rows = await fetchTypeRows(selectedCustomer.value, typeId);
          return { typeId, rows };
        } catch {
          return { typeId, rows: [] };
        }
      })
    );
    setPricingsByType(prev => {
      const next = { ...prev };
      for (const { typeId, rows } of loads) next[typeId] = rows;
      return next;
    });
  };

  const toggleExpand = async (typeId) => {
    const next = new Set(expanded);
    if (next.has(typeId)) {
      next.delete(typeId);
      setExpanded(next);
      return;
    }
    next.add(typeId);
    setExpanded(next);

    if (!selectedCustomer?.value) return;

    if (!pricingsByType[typeId]) {
      setLoadingByType((m) => ({ ...m, [typeId]: true }));
      try {
        const rows = await fetchTypeRows(selectedCustomer.value, typeId);
        setPricingsByType((p) => ({ ...p, [typeId]: rows }));
      } catch (e) {
        console.error(e);
        alert('Fehler beim Laden der Preisstaffeln.');
      } finally {
        setLoadingByType((m) => ({ ...m, [typeId]: false }));
      }
    }
  };

  // ===== Edit flow =====
  const startEdit = (machineTypeId, row) => {
    setEditingKey({ machineTypeId, typePricingId: row.typePricingId });
    setEditValue(moneyStr(row.pricePerUnit));
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    const { machineTypeId, typePricingId } = editingKey;
    const rows = pricingsByType[machineTypeId] || [];
    const row = rows.find((r) => r.typePricingId === typePricingId);
    if (!row) return;

    const parsed = parseFloat(String(editValue).replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      alert('Bitte gültigen Preis eingeben (z. B. 200,00).');
      return;
    }

    try {
      if (row.customerRentalPricingId) {
        await api.put(`/customer-rental-pricing/${row.customerRentalPricingId}`, { pricePerUnit: parsed });
        const updated = rows.map((r) =>
          r.typePricingId === typePricingId ? { ...r, pricePerUnit: parsed } : r
        );
        setPricingsByType((p) => ({ ...p, [machineTypeId]: sortPricings(updated) }));
      } else {
        const createRes = await api.post('/customer-rental-pricing', {
          customerId: selectedCustomer.value,
          machineTypeId: machineTypeId,
          minQuantity: row.minQuantity,
          maxQuantity: row.maxQuantity,
          pricePerUnit: parsed,
        });
        const updated = rows.map((r) =>
          r.typePricingId === typePricingId
            ? { ...r, customerRentalPricingId: createRes.data.id, pricePerUnit: createRes.data.pricePerUnit }
            : r
        );
        setPricingsByType((p) => ({ ...p, [machineTypeId]: sortPricings(updated) }));
      }
      cancelEdit();
      alert('Preis gespeichert.');
    } catch (e) {
      console.error(e);
      alert('Fehler beim Speichern des Preises.');
    }
  };

  const removeRowPricing = async (machineTypeId, row) => {
    if (!row.customerRentalPricingId) return;
    if (!window.confirm('Diese kundenspezifische Preisstaffel löschen?')) return;
    try {
      await api.delete(`/customer-rental-pricing/${row.customerRentalPricingId}`);
      const updated = (pricingsByType[machineTypeId] || []).map((r) =>
        r.typePricingId === row.typePricingId
          ? { ...r, customerRentalPricingId: null, pricePerUnit: r.originalPricePerUnit }
          : r
      );
      setPricingsByType((p) => ({ ...p, [machineTypeId]: sortPricings(updated) }));
      alert('Preisstaffel gelöscht.');
    } catch (e) {
      console.error(e);
      alert('Fehler beim Löschen.');
    }
  };

  // ===== Copy flow (FEHLERQUELLE behoben) =====
  const handleCopy = async () => {
    if (!selectedCustomer?.value) {
      alert('Bitte zuerst den Zielkunden wählen.');
      return;
    }
    if (!copyFromCustomer?.value) {
      alert('Bitte Quellkunden wählen.');
      return;
    }
    if (copyFromCustomer.value === selectedCustomer.value) {
      alert('Quelle und Ziel dürfen nicht identisch sein.');
      return;
    }

    try {
      setCopyBusy(true);
      const payload = {
        fromCustomerId: copyFromCustomer.value,
        toCustomerId: selectedCustomer.value,
        overwrite: !!copyOverwrite,
      };
      if (copyOnlyType?.value) payload.machineTypeId = copyOnlyType.value;

      const res = await api.post('/customer-rental-pricing/copy', payload);
      const msg = res?.data?.message || 'Kopieren abgeschlossen.';
      const created = res?.data?.created ?? 0;
      const updated = res?.data?.updated ?? 0;
      const skipped = res?.data?.skipped ?? 0;
      alert(`${msg}\nNeu: ${created} | Aktualisiert: ${updated} | Übersprungen: ${skipped}`);

      // Nach Kopie: betroffene Typen neu laden
      if (copyOnlyType?.value) {
        const typeId = copyOnlyType.value;
        if (isExpanded(typeId)) {
          try {
            const rows = await fetchTypeRows(selectedCustomer.value, typeId);
            setPricingsByType((p) => ({ ...p, [typeId]: rows }));
          } catch {}
        }
      } else {
        await reloadExpandedTypes();
      }
    } catch (e) {
      console.error(e);
      const err = e?.response?.data?.error || 'Fehler beim Kopieren.';
      alert(err);
    } finally {
      setCopyBusy(false);
    }
  };

  const handleDownloadPDF = async (machineTypeId) => {
    if (!selectedCustomer?.value || !machineTypeId) return;

    const pdfTab = window.open('', '_blank');

    try {
      const response = await api.get(
        `/customer-rental-pricing/${selectedCustomer.value}/${machineTypeId}/pdf`,
        { responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      if (pdfTab) {
        pdfTab.location.href = url;
      } else {
        window.open(url, '_blank');
      }

      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      if (pdfTab) pdfTab.close();
      console.error('Kundentyppreisliste konnte nicht geoeffnet werden:', error);
      alert('Kundentyppreisliste konnte nicht geoeffnet werden.');
    }
  };

  // ===== Render =====
  return (
    <div className="customer-rental-pricing">
      <Helmet>
        <title>Kunden-Vermietpreisliste</title>
        <meta name="description" content="Kunden-Vermietpreisliste – Typen & Preisstaffeln verwalten" />
      </Helmet>

      <h1>Kunden-Vermietpreisliste</h1>

      {/* Ziel-Kundenauswahl (zentriert, maxWidth 480) */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, maxWidth: 480, width: '100%' }}>
            <Select
              options={customerOptions}
              placeholder="Kunde suchen…"
              isClearable
              value={selectedCustomer}
              onChange={(opt) => {
                setSelectedCustomer(opt || null);
                setExpanded(new Set());
                setPricingsByType({});
                setEditingKey(null);

                // Copy-Panel zurücksetzen
                setCopyFromCustomer(null);
                setCopyOnlyType(null);
                setCopyOverwrite(false);
              }}
              styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
            />
          </div>
        </div>
      </div>

      {/* Copy-Panel */}
      {selectedCustomer?.value && (
        <div style={{ maxWidth: 1000, margin: '0 auto 12px' }}>
          <div style={{ padding: 12, border: '1px solid #ddd', borderRadius: 8, background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0 }}>Preise kopieren → {selectedCustomer.label}</h3>
            </div>

            <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'center' }}>
              <div>
                <label>Quellkunde</label>
                <Select
                  options={customerOptions}
                  placeholder="Kunde auswählen…"
                  isClearable
                  value={copyFromCustomer}
                  onChange={setCopyFromCustomer}
                  styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                />
              </div>

              <div>
                <label>Nur Typ (optional)</label>
                <Select
                  options={machineTypeOptions}
                  placeholder="Alle Typen"
                  isClearable
                  value={copyOnlyType}
                  onChange={setCopyOnlyType}
                  styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={copyOverwrite}
                    onChange={(e) => setCopyOverwrite(e.target.checked)}
                  />
                  Überschreiben
                </label>
                <button
                  onClick={handleCopy}
                  disabled={copyBusy || !copyFromCustomer?.value}
                  style={{
                    fontSize: 15,
                    padding: '8px 12px',
                    background: '#28a745',
                    color: '#ffffffff',
                    border: '1px solid #28a745',
                    borderRadius: 6,
                    cursor: copyBusy ? 'default' : 'pointer',
                    opacity: copyBusy ? 0.7 : 1
                  }}
                >
                  {copyBusy ? 'Kopiere…' : 'Preise kopieren'}
                </button>
              </div>
            </div>

            <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
              Tipp: Ohne Typen-Auswahl werden <b>alle</b> Typen vom Quellkunden kopiert.
              {' '}Mit „Überschreiben“ werden bestehende Staffeln beim Zielkunden aktualisiert.
            </div>
          </div>
        </div>
      )}

      {/* Typen & Preisstaffeln */}
      {selectedCustomer?.value ? (
        <>
          <div style={{ maxWidth: 1000, margin: '0 auto' }}>
            <h3 style={{ marginTop: 0 }}>Typen & Preisstaffeln – {selectedCustomer.label}</h3>
          </div>

          {/* >>> DIESEN GANZEN BLOCK EINSETZEN <<< */}
    <div style={{ marginTop: 10 }}>
      {machineTypes.map((t) => {
        const rows = pricingsByType[t.id] || [];
        const busy = !!loadingByType[t.id];

        return (
          <div key={t.id} style={{ marginBottom: 12 }}>
            {/* EIN gemeinsamer Wrapper für Header (grau) + Content (weiß) */}
            <div style={{ maxWidth: 1000, margin: '0 auto' }}>
              {/* Header als Button mit 100% Breite (wie in TypePricingList) */}
              <button
                onClick={() => toggleExpand(t.id)}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  padding: '10px 14px',                // identisch zu TypePricingList
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  background: '#D4D4D4',
                  color: '#534e4eff',
                  fontWeight: 600,
                  border: 'none',
                  borderRadius: 4
                }}
              >
                <span>{t.name}</span>
                <span></span>
              </button>

              {/* Weißer Content teilt sich denselben Wrapper → gleiche Breite */}
              {isExpanded(t.id) && (
                <div
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    background: '#fff',
                    padding: 12
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                    <button
                      onClick={() => handleDownloadPDF(t.id)}
                      style={{ padding: '6px 12px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
                      title="Kundentyppreisliste als PDF herunterladen"
                    >
                      PDF Preisliste
                    </button>
                  </div>
                  {busy ? (
                    <div style={{ fontStyle: 'italic' }}>Lade Staffeln…</div>
                  ) : rows.length === 0 ? (
                    <div style={{ marginBottom: 8 }}>Keine Staffeln vorhanden.</div>
                  ) : (
                    <>
                      {/* Kopfzeile (Grid) */}
                     <div
  style={{
    display: 'grid',
    gridTemplateColumns: '160px 160px 160px 160px 160px 160px', // fixierte Spalten, letzte = Aktionen
    gap: 10,
    padding: '6px 0',
    borderBottom: '1px solid #eee',
    fontWeight: 600
  }}
>
  <div>Von</div>
  <div>Bis</div>
  <div>Originalpreis (€)</div>
  <div>Preis/Einheit (€)</div>
  <div>Einheit</div>
  <div style={{ textAlign: 'left' }}>Aktionen</div>
</div>

                      {/* Zeilen (ul/li wie in TypePricingList) */}
                      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {rows.map((r) => {
                          const isEditing =
                            editingKey &&
                            editingKey.machineTypeId === t.id &&
                            editingKey.typePricingId === r.typePricingId;

                          return (
                            <li
  key={r.typePricingId}
    style={{
    display: 'grid',
    gridTemplateColumns: '160px 160px 160px 160px 160px 160px', // identisch zum Kopf
    gap: 10,
    padding: '8px 0',
    borderBottom: '1px solid #eee',
    alignItems: 'center'
  
  }}
>
  {/* Von */}
  <span>{r.minQuantity}</span>

  {/* Bis */}
  <span>{r.maxQuantity == null ? '∞' : r.maxQuantity}</span>

  {/* Originalpreis (nur Anzeige) - NEU */}
  <span>{moneyStr(r.originalPricePerUnit)}</span>

  {/* Preis/Einheit (editierbar) */}
  <span>
    {isEditing ? (
      <input
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        style={{ width: 120, padding: 6 }}
        title="Preis (z. B. 200,00)"
      />
    ) : r.discountApplied && !r.customerRentalPricingId ? (
      <>
        <span style={{ color: 'green', fontWeight: 600 }}>
          {moneyStr(r.pricePerUnit)}
        </span>
        <span style={{ fontSize: 10, color: '#888', marginLeft: 6 }}>
          ({r.discountApplied}% Gruppenrabatt)
        </span>
      </>
    ) : (
      <>{moneyStr(r.pricePerUnit)}</>
    )}
  </span>

  {/* Einheit */}
  <span>{r.unit?.name || '-'}</span>

  {/* Aktionen */}
  <div style={{ display: 'flex', gap: 6 }}>
    {isEditing ? (
      <>
        <button
          onClick={saveEdit}
          style={{
            padding: '4px 8px',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
          }}
        >
          ✅
        </button>
        <button
          onClick={cancelEdit}
          style={{
            padding: '4px 8px',
            background: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
           
          }}
        >
          X
        </button>
      </>
    ) : r.customerRentalPricingId ? (
      <>
        <button
          onClick={() => startEdit(t.id, r)}
          style={{
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '4px 8px',
          }}
        >
          edit
        </button>
        <button
          onClick={() => removeRowPricing(t.id, r)}
          style={{
            background: '#dc3545',
            color: '#fff',
            border: 'none',
            borderRadius: 4,
            padding: '4px 8px',
          }}
        >
          X
        </button>
      </>
    ) : (
      <button
        onClick={() => startEdit(t.id, r)}
        style={{
          background: '#28a745',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          padding: '4px 8px',
        }}
      >
        +
      </button>
    )}
  </div>
</li>

                          );
                        })}
                      </ul>

                      {rows.some((r) => r.discountApplied && !r.customerRentalPricingId) && (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
                          <span style={{ color: 'green', fontWeight: 600 }}>*</span> Preis inkl. Kundengruppenrabatt
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
    {/* >>> ENDE: DIESEN BLOCK EINSETZEN <<< */}
        </>
      ) : (
        <div style={{ fontStyle: 'italic' }}></div>
      )}
    </div>
  );
};

export default CustomerRentalPricingList;
